import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const DevicePaths = {
    // Highly detailed laptop
    laptop: {
        // Outer screen Bezel + Screen Area
        screen: "M 15 25 L 85 25 A 2 2 0 0 1 87 27 L 87 60 L 13 60 L 13 27 A 2 2 0 0 1 15 25 Z",
        // Keyboard Base + Deck + Trackpad outline
        base: "M 12 60 L 88 60 L 96 72 A 2 2 0 0 1 94 75 L 6 75 A 2 2 0 0 1 4 72 L 12 60 Z",
        viewBox: "0 0 100 100",
        label: "WEB",
        color: "#ff014f" // designColor
    },
    // Highly detailed tablet (iPad portrait)
    tablet: {
        // Tablet outer body (Portrait)
        screen: "M 25 10 L 75 10 A 4 4 0 0 1 79 14 L 79 86 A 4 4 0 0 1 75 90 L 25 90 A 4 4 0 0 1 21 86 L 21 14 A 4 4 0 0 1 25 10 Z",
        // Tablet Inner Screen edge (Portrait)
        base: "M 25 18 L 75 18 L 75 82 L 25 82 Z",
        viewBox: "0 0 100 100",
        label: "TABLET",
        color: "#a855f7" // purple
    },
    // Highly detailed phone (iPhone portrait)
    phone: {
        // Phone outer body
        screen: "M 35 10 L 65 10 A 6 6 0 0 1 71 16 L 71 84 A 6 6 0 0 1 65 90 L 35 90 A 6 6 0 0 1 29 84 L 29 16 A 6 6 0 0 1 35 10 Z",
        // Phone Inner Screen
        base: "M 33 18 L 67 18 L 67 82 L 33 82 Z",
        viewBox: "0 0 100 100",
        label: "MOBILE",
        color: "#06b6d4" // cyan
    }
};

const AnimatedAppLoader = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const order = ["laptop", "tablet", "phone"];

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentStep((prev) => (prev + 1) % order.length);
        }, 1500); // Change device every 1.5 seconds

        return () => clearInterval(interval);
    }, [order.length]);

    const currentDevice = DevicePaths[order[currentStep]];

    return (
        <div className="flex flex-col items-center justify-center min-h-[300px] w-full bg-[#212428]">
            <div className="relative w-40 h-40 sm:w-48 sm:h-48 flex flex-col items-center justify-center">

                {/* Background ambient pulse */}
                <motion.div
                    animate={{
                        backgroundColor: currentDevice.color,
                        scale: [1, 1.2, 1],
                        opacity: [0.1, 0.2, 0.1]
                    }}
                    transition={{ duration: 1.5, ease: "easeInOut" }}
                    className="absolute inset-0 m-auto w-3/4 h-3/4 blur-[40px] rounded-full z-0"
                />

                {/* SVG Morphing Canvas */}
                <svg viewBox="0 0 100 100" className="w-full h-full relative z-10 drop-shadow-[0_0_15px_rgba(255,1,79,0.2)]">
                    <motion.g
                        initial={false}
                        animate={{ stroke: currentDevice.color }}
                        transition={{ duration: 0.8 }}
                        fill="transparent"
                        strokeWidth="3"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                    >
                        {/* Primary Shape (Screen/Outer) */}
                        <motion.path
                            d={currentDevice.screen}
                            initial={false}
                            animate={{ d: currentDevice.screen }}
                            transition={{ type: "spring", bounce: 0.2, duration: 0.8 }}
                        />
                        {/* Secondary Shape (Base/Inner) */}
                        <motion.path
                            d={currentDevice.base}
                            initial={false}
                            animate={{ d: currentDevice.base, strokeWidth: order[currentStep] === "laptop" ? 3 : 2 }}
                            transition={{ type: "spring", bounce: 0.2, duration: 0.8 }}
                        />
                    </motion.g>

                    {/* Pulsing screen contents / "Code" */}
                    <motion.g
                        initial={{ opacity: 0 }}
                        animate={{ opacity: [0.3, 0.8, 0.3] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        fill={currentDevice.color}
                        className="opacity-50"
                    >
                        {order[currentStep] === "laptop" && (
                            <>
                                {/* Simulated IDE UI on Laptop Screen */}
                                <rect x="20" y="32" width="60" height="2" rx="1" />
                                <rect x="20" y="38" width="40" height="2" rx="1" />
                                <rect x="20" y="44" width="25" height="2" rx="1" />
                                <rect x="20" y="50" width="50" height="2" rx="1" />

                                {/* Keyboard Keys layout suggestion on base */}
                                <rect x="30" y="63" width="40" height="3" rx="1" fill="#fff" opacity="0.3" />
                                <rect x="35" y="68" width="30" height="3" rx="1" fill="#fff" opacity="0.3" />

                                {/* Trackpad */}
                                <rect x="42" y="73" width="16" height="1" fill="#fff" opacity="0.5" />
                            </>
                        )}
                        {order[currentStep] === "tablet" && (
                            <>
                                {/* Simulated tablet UI (Portrait) */}
                                <rect x="35" y="30" width="30" height="4" rx="2" />
                                <rect x="35" y="40" width="20" height="4" rx="2" />
                                <rect x="35" y="50" width="25" height="4" rx="2" />

                                {/* Tablet Camera & Home Button on bezels (Top/Bottom) */}
                                <circle cx="50" cy="14" r="1.5" fill="#fff" opacity="0.4" />
                                <circle cx="50" cy="86" r="2.5" fill="transparent" stroke="#fff" strokeWidth="1" opacity="0.4" />
                            </>
                        )}
                        {order[currentStep] === "phone" && (
                            <>
                                {/* Simulated phone UI */}
                                <rect x="42" y="32" width="16" height="4" rx="2" />
                                <rect x="42" y="42" width="10" height="4" rx="2" />
                                <rect x="42" y="52" width="12" height="4" rx="2" />
                                <rect x="42" y="62" width="16" height="4" rx="2" />

                                {/* Phone Notch / Earpiece */}
                                <rect x="42" y="13" width="16" height="2" rx="1" fill="#fff" opacity="0.4" />

                                {/* Phone Home Indicator (Bottom Bar) */}
                                <rect x="40" y="85" width="20" height="2" rx="1" fill="#fff" opacity="0.4" />
                            </>
                        )}
                    </motion.g>
                </svg>

                {/* Animated Labels */}
                <div className="absolute -bottom-8 w-full flex flex-col items-center overflow-hidden h-8">
                    <AnimatePresence mode="wait">
                        <motion.span
                            key={currentDevice.label}
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: -20, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                            className="font-titleFont text-xs sm:text-sm font-bold tracking-[0.3em]"
                            style={{ color: currentDevice.color }}
                        >
                            {currentDevice.label}
                        </motion.span>
                    </AnimatePresence>
                </div>
            </div>

            {/* Subtext */}
            <div className="mt-16 flex items-center gap-1 font-mono text-[10px] sm:text-xs text-gray-500 uppercase tracking-widest">
                <span>Building Experiences</span>
                <motion.span animate={{ opacity: [0, 1, 0] }} transition={{ duration: 1.5, repeat: Infinity, delay: 0 }}>.</motion.span>
                <motion.span animate={{ opacity: [0, 1, 0] }} transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}>.</motion.span>
                <motion.span animate={{ opacity: [0, 1, 0] }} transition={{ duration: 1.5, repeat: Infinity, delay: 0.4 }}>.</motion.span>
            </div>
        </div>
    );
};

export default AnimatedAppLoader;
